import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* Why it works — a standalone public explainer page.
   Mounted at /why-it-works as its own route (no app shell, no sidebar).
   Reuses the legal-page header chrome for the back button + brand, then
   tells the story of how Community Ninja reads for meaning instead of
   keywords. The hero swaps between light/dark art via [data-theme] in CSS
   so it stays in sync with the global theme without any JS. */

const COMPANY_NAME = "Community Ninja";

const WHY_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Why Community Ninja works",
  description:
    "Most tools match keywords. Community Ninja reads for intent and meaning, builds a profile of your product, filters the noise, and tells you why every thread matters.",
};

/* The phrases people actually use right before they go looking for a fix. */
const INTENT_PHRASES = [
  "I'm struggling with…",
  "Does anyone know a tool for…",
  "How are you all handling…",
  "What's the best way to…",
  "Is there an easier way to do this?",
];

/* What we learn about your product before a single scan runs. */
const PROFILE_POINTS = [
  "who your product actually helps",
  "the problems it takes off the table",
  "the use cases that really matter",
  "how real people describe the pain",
  "which communities are worth watching",
  "the topics worth ignoring",
];

/* What ships attached to every opportunity. */
const REASON_POINTS = [
  "why this matched",
  "the pain point it maps to",
  "the intent we detected",
  "why it may be worth a reply",
];

/* The body, as a numbered editorial run. Each entry can carry an optional
   set of quoted phrases or a plain bullet list, plus a closing note. */
const SECTIONS = [
  {
    n: "01",
    title: "Built for intent, not mentions",
    lede:
      "We do not wait for someone to say your name. We watch for the moments that come right before a decision.",
    phrases: INTENT_PHRASES,
    note: "That is where the real opportunity lives, long before a brand ever gets named.",
  },
  {
    n: "02",
    title: "It learns your product first",
    lede:
      "Before a single scan runs, Community Ninja builds a working picture of your business:",
    bullets: PROFILE_POINTS,
    note: "That picture becomes the lens for everything that follows.",
  },
  {
    n: "03",
    title: "It cuts the noise",
    lede:
      "A thread can be related and still be useless. Community Ninja weighs every conversation on relevance, intent, context, freshness, and quality, then quietly drops the weak matches before they reach your brief.",
  },
  {
    n: "04",
    title: "You get the reason, not just the result",
    lede: "Every opportunity comes with a plain-English read:",
    bullets: REASON_POINTS,
    note: "So you are never staring at a wall of links. You know why each one earned its place.",
  },
  {
    n: "05",
    title: "Human first, always",
    lede:
      "Community Ninja helps you find the right conversations. It never posts, auto-comments, or fakes interest where none exists. You read the context, decide what matters, and step in only when it genuinely makes sense.",
  },
];

function WhyItWorksScreen() {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="expl-page screen-enter">
      <Seo
        title="Why it works"
        description="Most tools match keywords. Community Ninja reads for intent and meaning, then surfaces the Reddit conversations where your product is actually relevant."
        keywords={[
          "reddit intent detection",
          "semantic social listening",
          "customer discovery",
          "buyer intent",
          "reddit monitoring",
        ]}
        jsonLd={WHY_JSONLD}
      />

      <header className="legal-top">
        <button
          type="button"
          className="legal-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/");
          }}
          aria-label="Go back"
        >
          <window.I.ArrowLeft size={14} />
          <span>Back</span>
        </button>
        <Link to="/" className="legal-brand" aria-label={`${COMPANY_NAME} home`}>
          <span className="legal-brand-name">{COMPANY_NAME}</span>
        </Link>
      </header>

      <article className="expl-shell">
        {/* ---------- Hero ---------- */}
        <div className="expl-hero">
          <div className="expl-eyebrow">
            <span className="expl-eyebrow-dot" />
            <span>Why it works</span>
          </div>
          <h1 className="expl-title">
            Most tools match words.{" "}
            <span className="wv4-serif">We match meaning.</span>
          </h1>
          <p className="expl-lede">
            Your potential customers rarely describe their problem in the words you
            would pick. They ask messy questions, vent in comments, compare
            workflows, and describe the pain in their own language. Community
            Ninja reads for what they actually mean, then lines it up against
            what your product really solves.
          </p>
        </div>

        <figure className="expl-art">
          <img
            className="expl-art-img is-dark"
            src="/assets/why-it-works-dark.png"
            alt="How Community Ninja turns conversations into ranked signals"
            loading="eager"
          />
          <img
            className="expl-art-img is-light"
            src="/assets/why-it-works-light.png"
            alt="How Community Ninja turns conversations into ranked signals"
            loading="eager"
          />
        </figure>

        {/* ---------- Body ---------- */}
        <div className="expl-body">
          {SECTIONS.map((s) => (
            <section className="expl-section" key={s.n}>
              <div className="expl-section-num">{s.n}</div>
              <div className="expl-section-content">
                <h2 className="expl-section-title">{s.title}</h2>
                <p className="expl-section-lede">{s.lede}</p>

                {s.phrases && (
                  <ul className="expl-phrases">
                    {s.phrases.map((phrase) => (
                      <li className="expl-phrase" key={phrase}>
                        {phrase}
                      </li>
                    ))}
                  </ul>
                )}

                {s.bullets && (
                  <ul className="expl-list">
                    {s.bullets.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}

                {s.note && <p className="expl-note">{s.note}</p>}
              </div>
            </section>
          ))}
        </div>

        {/* ---------- Closing ---------- */}
        <section className="expl-result">
          <span className="expl-result-eyebrow">The result</span>
          <h2 className="expl-result-title">
            A focused daily brief of conversations where you{" "}
            <span className="wv4-serif">actually belong.</span>
          </h2>
          <p className="expl-result-sub">
            The Reddit threads where your product, your market, or your expertise
            is genuinely relevant. Less scrolling. More of the right
            conversations.
          </p>
          <Link to="/" className="btn btn-primary btn-lg expl-result-cta">
            <window.I.Sparkles size={15} />
            Build my radar
          </Link>
        </section>
      </article>

      {window.LegalFooter && <window.LegalFooter variant="page" />}
    </div>
  );
}

window.WhyItWorksScreen = WhyItWorksScreen;

export default WhyItWorksScreen;
